import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Typography
} from '@mui/material';
import CurrencyTextField from './CurrencyTextField';
import PartCategoryField from './PartCategoryField';
import PartLabelsField from './PartLabelsField';
import { partService } from '../services/partService';

const emptyPartForm = () => ({
  part_number: '',
  description: '',
  unit_cost: '',
  unit_price: '',
  quantity_on_hand: '',
  minimum_quantity: '1',
  supplier: '',
  category: '',
  labels: [],
  is_active: true
});

const rowToForm = (row) => ({
  part_number: row.part_number ?? '',
  description: row.part_description ?? row.description ?? '',
  unit_cost: row.unit_cost != null ? String(row.unit_cost) : '',
  unit_price: row.unit_price != null ? String(row.unit_price) : '',
  quantity_on_hand: row.quantity_on_hand != null ? String(row.quantity_on_hand) : '',
  minimum_quantity: row.minimum_quantity != null ? String(row.minimum_quantity) : '1',
  supplier: row.supplier ?? '',
  category: row.category ?? '',
  labels: Array.isArray(row.labels) ? row.labels : [],
  is_active: row.is_active !== false
});

function usageSummary(part) {
  const quotes = Number(part?.quote_references) || 0;
  const orders = Number(part?.order_references) || 0;
  const invoices = Number(part?.invoice_references) || 0;
  const slips = Number(part?.packing_slip_references) || 0;
  const bits = [];
  if (quotes > 0) bits.push(`${quotes} quote${quotes === 1 ? '' : 's'}`);
  if (orders > 0) bits.push(`${orders} order${orders === 1 ? '' : 's'}`);
  if (invoices > 0) bits.push(`${invoices} invoice${invoices === 1 ? '' : 's'}`);
  if (slips > 0) bits.push(`${slips} packing slip${slips === 1 ? '' : 's'}`);
  return { total: quotes + orders + invoices + slips, bits };
}

/**
 * Edit-part dialog with local form state so typing does not re-render the Parts table.
 */
const PartEditDialog = ({ open, part, onClose, onSaved }) => {
  const [formData, setFormData] = useState(emptyPartForm());
  const [categories, setCategories] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [renameConfirmOpen, setRenameConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !part) return;
    setFormData(rowToForm(part));
    setError('');
    setRenameConfirmOpen(false);
    let cancelled = false;
    Promise.all([partService.getCategories(), partService.getLabels()])
      .then(([catRes, labRes]) => {
        if (cancelled) return;
        setCategories(Array.isArray(catRes?.data) ? catRes.data : []);
        setLabelOptions(Array.isArray(labRes?.data) ? labRes.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setCategories([]);
        setLabelOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, part]);

  const handleClose = () => {
    if (!saving) onClose?.();
  };

  const numberChanged = () => {
    const next = String(formData.part_number || '').trim();
    const prev = String(part?.part_number || '').trim();
    return next !== prev;
  };

  const buildPayload = () => {
    const payload = {
      ...formData,
      part_number: String(formData.part_number || '').trim(),
      category: formData.category?.trim() || '',
      label_ids: (formData.labels || []).map((l) => l.id).filter(Boolean),
      unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
      unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
      quantity_on_hand:
        formData.quantity_on_hand != null && formData.quantity_on_hand !== ''
          ? parseInt(formData.quantity_on_hand, 10)
          : null,
      minimum_quantity:
        formData.minimum_quantity != null && formData.minimum_quantity !== ''
          ? parseInt(formData.minimum_quantity, 10)
          : null
    };
    delete payload.labels;
    return payload;
  };

  const persist = async () => {
    if (!part?.id) return;
    try {
      setSaving(true);
      setError('');
      await partService.update(part.id, buildPayload());
      setRenameConfirmOpen(false);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Error updating part:', err);
      setError(err.response?.data?.message || 'Failed to update part');
      setRenameConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!part?.id) return;
    if (!formData.description?.trim()) {
      setError('Description is required.');
      return;
    }
    if (!String(formData.part_number || '').trim()) {
      setError('Part number is required.');
      return;
    }
    if (!/^[A-Za-z0-9-]+$/.test(String(formData.part_number).trim())) {
      setError('Part number may contain only letters, numbers, and dashes.');
      return;
    }

    if (numberChanged()) {
      const { total } = usageSummary(part);
      if (total > 0) {
        setRenameConfirmOpen(true);
        return;
      }
    }
    await persist();
  };

  const usage = usageSummary(part);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Part</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Part Number"
                value={formData.part_number}
                onChange={(e) => setFormData((f) => ({ ...f, part_number: e.target.value }))}
                required
                helperText="Changing this updates the catalog only; existing quotes/orders keep their snapshot."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CurrencyTextField
                fullWidth
                size="small"
                label="Unit Cost"
                value={formData.unit_cost}
                onChange={(e) => setFormData((f) => ({ ...f, unit_cost: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <CurrencyTextField
                fullWidth
                size="small"
                label="Unit Price"
                value={formData.unit_price}
                onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Qty on Hand"
                type="number"
                value={formData.quantity_on_hand}
                onChange={(e) => setFormData((f) => ({ ...f, quantity_on_hand: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Minimum Qty"
                type="number"
                value={formData.minimum_quantity}
                onChange={(e) => setFormData((f) => ({ ...f, minimum_quantity: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Supplier"
                value={formData.supplier}
                onChange={(e) => setFormData((f) => ({ ...f, supplier: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PartCategoryField
                value={formData.category}
                onChange={(category) => setFormData((f) => ({ ...f, category }))}
                options={categories}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <PartLabelsField
                value={formData.labels}
                onChange={(labels) => setFormData((f) => ({ ...f, labels }))}
                options={labelOptions}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameConfirmOpen} onClose={() => !saving && setRenameConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change part number?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Change catalog part number from <strong>{part?.part_number}</strong> to{' '}
            <strong>{String(formData.part_number || '').trim()}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This part is on {usage.bits.join(', ') || 'documents'}. Existing quotes, orders, invoices, and packing
            slips will keep the old number; new documents will use the corrected number.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={persist} disabled={saving}>
            {saving ? 'Saving…' : 'Change part number'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartEditDialog;
